import { Dialog, DialogPanel, Transition, TransitionChild } from "@headlessui/react";
import clsx from "clsx";
import type { PropsWithChildren } from "react";
import { Fragment, useCallback, useRef } from "react";

type ModalProps = PropsWithChildren & {
	open: boolean;
	setOpen: (bool: boolean) => void;
	closeOnOutsideClick?: boolean;
	widthClassName?: string;
	theme?: "light" | "dark";
	/**
	 * Renders the children until the leave transition ends, not only while `open` is true. The children must render
	 * without the data of the closed state, because the data may turn `null` before the transition ends.
	 */
	keepChildrenUntilLeft?: boolean;
}

const Modal = ({
	open,
	setOpen,
	closeOnOutsideClick = true,
	children,
	theme = "light",
	widthClassName,
	keepChildrenUntilLeft = false,
}: ModalProps) => {
	const outsideClickHandler = useCallback(() => {
		if (closeOnOutsideClick) {
			setOpen(false);
		}
	}, [closeOnOutsideClick, setOpen]);

	const cancelButtonRef = useRef(null);

	return (
		<Transition show={open}
					as={Fragment}>
			<Dialog as="div"
					className="relative z-50"
					initialFocus={cancelButtonRef}
					onClose={outsideClickHandler}>
				<TransitionChild
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
				</TransitionChild>
				<div className="fixed inset-0 z-10 w-screen overflow-y-auto">
					<div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
						<TransitionChild
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
							enterTo="opacity-100 translate-y-0 sm:scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 translate-y-0 sm:scale-100"
							leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
						>
							{/*
							 * Below `sm` the panel has the full width (a custom `widthClassName` replaces it), so its size does not follow its content. Its height stops at
							 * the screen within the safe-area insets, and the content scrolls in y.
							 */}
							<DialogPanel className={clsx("relative max-h-[calc(100dvh-2rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] overflow-y-auto rounded-lg px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:max-h-none sm:overflow-hidden sm:p-6",
								widthClassName || "w-full sm:max-w-lg",
								theme === "light" ? "bg-white" : "bg-gray-100")}
							>
								{(open || keepChildrenUntilLeft) && children}
							</DialogPanel>
						</TransitionChild>

					</div>
				</div>
			</Dialog>
		</Transition>
	);
};

export default Modal;
